import React, { useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Flex,
  HStack,
  VStack,
  Text,
  Divider,
  IconButton,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Container,
  Badge,
  useDisclosure,
  Avatar,
} from "@chakra-ui/react";
import {
  FiMenu,
  FiHome,
  FiUsers,
  FiBriefcase,
  FiCreditCard,
  FiSettings,
  FiLogOut,
  FiUser,
  FiBell,
  FiSearch,
  FiFolderPlus,
  FiBarChart2,
  FiFileText,
  FiTrendingUp,
  FiCalendar,
} from "react-icons/fi";

// ✅ Import both dashboard components
import CompanyDashboard from "../../components/dashboard/CompanyDashboard";
import StaffDashboard from "../../components/dashboard/StaffDashboard";

import UserManagement from "../../components/company/UserManagement";
import CompanyProfile from "../../components/company/CompanyProfile";
import SubscriptionDetails from "../../components/company/SubscriptionDetails";
import Settings from "../../components/settings/Settings";

// Project Management Components
import ProjectManagement from "../../components/projects/ProjectManagement";
import ProjectDetails from "../../components/projects/ProjectDetails";
import ProjectForm from "../../components/projects/ProjectForm";

// Progress components
import ProjectProgressDashboard from "../../components/projects/ProjectProgressDashboard";
import MonthlyProgressReport from "../../components/projects/MonthlyProgressReport";
import ProgressTracker from "../../components/projects/ProgressTracker";

// Company Components
import CompanyMasterDataManager from "../../components/company/CompanyMasterDataManager";

// Legacy imports
import ProgressTracking from "../../components/progress/ProgressTracking";
import MonthlyProgress from "../../components/progress/MonthlyProgress";
import ProjectReports from "../../components/reports/ProjectReports";

import { logoutUser } from "../../store/slices/authSlice";
import { ProgressProvider } from "../../contexts/ProgressContext";

const SIDEBAR_WIDTH = {
  base: "0px",
  lg: "280px",
};

const CompanyPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, company, subscription } = useSelector((state) => state.auth);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // ✅ Check if user is admin
  const isAdmin = user?.role === "company_admin" || user?.role === "super_admin";

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/auth/login");
  };

  // ✅ Updated menu items with role-based visibility
  const menuItems = [
    {
      text: "Dashboard",
      icon: <FiHome size={20} />,
      path: "/dashboard/company",
      description: isAdmin ? "Overview & Analytics" : "My Projects",
      visible: true, // Everyone sees dashboard
    },
    {
      text: "Projects",
      icon: <FiFolderPlus size={20} />,
      path: "/dashboard/company/projects",
      description: "Manage Projects",
      visible: true, // Everyone sees projects
    },
    {
      text: "Team Management",
      icon: <FiUsers size={20} />,
      path: "/dashboard/company/users",
      description: "Manage Users",
      visible: isAdmin, // ✅ Admin only
    },
    {
      text: "Company Profile",
      icon: <FiBriefcase size={20} />,
      path: "/dashboard/company/profile",
      description: "Company Settings",
      visible: isAdmin, // ✅ Admin only
    },
    {
      text: "Subscription",
      icon: <FiCreditCard size={20} />,
      path: "/dashboard/company/subscription",
      description: "Billing & Plans",
      visible: isAdmin, // ✅ Admin only (staff don't need to see billing)
    },
    {
      text: "Custom Master Data",
      icon: <FiFileText size={20} />,
      path: "/dashboard/company/master-data",
      description: "Manage Activities & SubTasks",
      visible: true, // Everyone can access
    },
    {
      text: "Settings",
      icon: <FiSettings size={20} />,
      path: "/dashboard/company/settings",
      description: "App Settings",
      visible: true, // Everyone can access personal settings
    },
  ];

  // ✅ Filter menu items based on visibility
  const filteredMenuItems = menuItems.filter((item) => item.visible);

  const getPageTitle = () => {
    const currentPath = location.pathname;
    if (currentPath.includes("/projects/new")) return "Create New Project";
    if (currentPath.includes("/projects/") && currentPath.includes("/edit"))
      return "Edit Project";
    if (
      currentPath.includes("/projects/") &&
      currentPath.includes("/progress-dashboard")
    )
      return "Project Progress Dashboard";
    if (currentPath.includes("/projects/") && currentPath.includes("/progress"))
      return "Project Progress Tracking";
    if (currentPath.includes("/projects/") && currentPath.includes("/monthly"))
      return "Monthly Progress Report";
    if (currentPath.includes("/projects/") && currentPath.includes("/reports"))
      return "Project Reports";
    if (
      currentPath.includes("/projects/") &&
      currentPath.match(/\/projects\/[^\/]+$/)
    )
      return "Project Details";
    if (currentPath.includes("/projects")) return "Project Management";

    const currentItem = filteredMenuItems.find(
      (item) => item.path === currentPath
    );
    return currentItem?.text || "Dashboard";
  };

  const SidebarContent = () => (
    <Box
      h="100vh"
      w="280px"
      bg="linear-gradient(180deg, #1a202c 0%, #2d3748 100%)"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgGradient:
          "linear(to-b, rgba(66, 153, 225, 0.1), rgba(159, 122, 234, 0.05))",
        pointerEvents: "none",
      }}
    >
      {/* Company Brand Section */}
      <Box px={6} py={8} position="relative">
        <HStack spacing={3} mb={2}>
          <Box
            w={10}
            h={10}
            borderRadius="xl"
            bgGradient="linear(135deg, #667eea 0%, #764ba2 100%)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            fontWeight="bold"
            fontSize="lg"
            boxShadow="0 8px 32px rgba(102, 126, 234, 0.3)"
          >
            {company?.name?.charAt(0) || "C"}
          </Box>
          <VStack align="start" spacing={0} flex="1" minW="0">
            <Text
              color="white"
              fontSize="lg"
              fontWeight="bold"
              letterSpacing="tight"
              isTruncated
            >
              {company?.name || "Company Dashboard"}
            </Text>
            <Badge
              colorScheme={
                subscription?.plan === "enterprise"
                  ? "purple"
                  : subscription?.plan === "premium"
                  ? "blue"
                  : "green"
              }
              size="sm"
              variant="solid"
              borderRadius="full"
              px={2}
            >
              {subscription?.plan || "Free"}
            </Badge>
          </VStack>
        </HStack>
      </Box>

      <Box px={4}>
        <Divider borderColor="whiteAlpha.200" />
      </Box>

      {/* User Info */}
      <Box px={6} py={4}>
        <HStack spacing={3}>
          <Avatar
            size="md"
            name={`${user?.firstName} ${user?.lastName}`}
            bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            color="white"
          />
          <VStack align="start" spacing={0} flex="1" minW="0">
            <Text color="white" fontSize="sm" fontWeight="semibold" isTruncated>
              {user?.firstName} {user?.lastName}
            </Text>
            <Text color="gray.400" fontSize="xs">
              {isAdmin ? "Administrator" : user?.role === "staff" ? "Team Member" : "Staff"}
            </Text>
          </VStack>
          <Badge
            colorScheme="green"
            variant="solid"
            fontSize="8px"
            borderRadius="full"
          >
            ONLINE
          </Badge>
        </HStack>
      </Box>

      <Box px={4} mb={4}>
        <Divider borderColor="whiteAlpha.200" />
      </Box>

      {/* Navigation Menu */}
      <VStack spacing={1} px={4} align="stretch" flex="1" overflowY="auto">
        {filteredMenuItems.map((item) => {
          const isActive =
            location.pathname === item.path ||
            (item.path === "/dashboard/company/projects" &&
              location.pathname.startsWith("/dashboard/company/projects"));

          return (
            <Box key={item.text} position="relative">
              <Button
                variant="ghost"
                leftIcon={item.icon}
                justifyContent="flex-start"
                w="full"
                h="48px"
                px={4}
                bg={isActive ? "whiteAlpha.100" : "transparent"}
                color={isActive ? "white" : "gray.300"}
                _hover={{
                  bg: isActive ? "whiteAlpha.200" : "whiteAlpha.100",
                  color: "white",
                  transform: "translateX(4px)",
                }}
                _active={{ bg: "whiteAlpha.200" }}
                fontWeight={isActive ? "semibold" : "medium"}
                fontSize="14px"
                borderRadius="xl"
                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                onClick={() => navigate(item.path)}
                boxShadow={
                  isActive ? "0 4px 20px rgba(102, 126, 234, 0.3)" : "none"
                }
                position="relative"
                overflow="hidden"
                _before={
                  isActive
                    ? {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "3px",
                        bgGradient: "linear(to-b, #667eea, #764ba2)",
                        borderRadius: "full",
                      }
                    : {}
                }
              >
                <VStack align="start" spacing={0} flex="1">
                  <Text
                    isTruncated
                    fontSize="14px"
                    fontWeight={isActive ? "semibold" : "medium"}
                  >
                    {item.text}
                  </Text>
                  {item.description && (
                    <Text
                      isTruncated
                      fontSize="10px"
                      color="gray.400"
                      opacity={0.8}
                    >
                      {item.description}
                    </Text>
                  )}
                </VStack>
              </Button>
              {isActive && (
                <Box
                  position="absolute"
                  right={4}
                  top="50%"
                  transform="translateY(-50%)"
                  w={2}
                  h={2}
                  bg="cyan.400"
                  borderRadius="full"
                  boxShadow="0 0 10px rgba(0, 255, 255, 0.6)"
                />
              )}
            </Box>
          );
        })}
      </VStack>

      {/* Bottom Status Section */}
      <Box p={4}>
        <Box
          p={4}
          bg="whiteAlpha.50"
          borderRadius="xl"
          border="1px solid"
          borderColor="whiteAlpha.100"
          backdropFilter="blur(10px)"
        >
          <VStack spacing={2}>
            <Text color="gray.300" fontSize="xs" textAlign="center">
              {isAdmin ? "Subscription Status" : "Account Status"}
            </Text>
            <HStack spacing={2}>
              <Box
                w={2}
                h={2}
                bg={
                  subscription?.status === "active" ? "green.400" : "orange.400"
                }
                borderRadius="full"
              />
              <Text
                color={
                  subscription?.status === "active" ? "green.400" : "orange.400"
                }
                fontSize="xs"
              >
                {isAdmin 
                  ? (subscription?.status === "active" ? "Active Plan" : "Upgrade Available")
                  : "Active Member"
                }
              </Text>
            </HStack>
          </VStack>
        </Box>
      </Box>
    </Box>
  );

  // ✅ Dashboard Component Wrapper - Routes to correct dashboard
  const DashboardWrapper = () => {
    if (isAdmin) {
      return <CompanyDashboard />;
    }
    return <StaffDashboard />;
  };

  return (
    <Box minH="100vh" bg="#0f1419" w="100vw" overflow="hidden">
      {/* Top Header */}
      <Box
        as="header"
        position="fixed"
        top={0}
        left={SIDEBAR_WIDTH}
        right={0}
        h="70px"
        bg="linear-gradient(135deg, rgba(26, 32, 44, 0.95) 0%, rgba(45, 55, 72, 0.95) 100%)"
        backdropFilter="blur(20px)"
        borderBottom="1px solid"
        borderColor="whiteAlpha.100"
        zIndex={999}
        boxShadow="0 4px 32px rgba(0, 0, 0, 0.1)"
      >
        <Flex alignItems="center" h="full" px={6}>
          <IconButton
            aria-label="open drawer"
            icon={<FiMenu color="white" />}
            onClick={onOpen}
            display={{ base: "flex", lg: "none" }}
            variant="ghost"
            color="white"
            _hover={{ bg: "whiteAlpha.100" }}
            mr={4}
          />

          <VStack align="start" spacing={0} flex="1">
            <Text
              color="white"
              fontSize="xl"
              fontWeight="bold"
              letterSpacing="tight"
            >
              {getPageTitle()}
            </Text>
            <Text color="gray.400" fontSize="sm">
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </Text>
          </VStack>

          <HStack spacing={4}>
            <IconButton
              aria-label="search"
              icon={<FiSearch />}
              variant="ghost"
              color="gray.300"
              _hover={{ bg: "whiteAlpha.100", color: "white" }}
              borderRadius="xl"
            />
            <Box position="relative">
              <IconButton
                aria-label="notifications"
                icon={<FiBell />}
                variant="ghost"
                color="gray.300"
                _hover={{ bg: "whiteAlpha.100", color: "white" }}
                borderRadius="xl"
              />
              <Badge
                position="absolute"
                top="-1"
                right="-1"
                colorScheme="red"
                borderRadius="full"
                boxSize="18px"
                fontSize="9px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                2
              </Badge>
            </Box>

            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                p={0}
                minW="auto"
                h="auto"
                _hover={{ transform: "scale(1.05)" }}
                transition="transform 0.2s"
              >
                <Avatar
                  size="sm"
                  name={`${user?.firstName} ${user?.lastName}`}
                  bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  color="white"
                  border="2px solid"
                  borderColor="whiteAlpha.300"
                />
              </MenuButton>
              <MenuList
                bg="gray.800"
                borderColor="whiteAlpha.200"
                boxShadow="0 20px 40px rgba(0, 0, 0, 0.3)"
              >
                <MenuItem
                  icon={<FiUser />}
                  bg="transparent"
                  color="white"
                  _hover={{ bg: "whiteAlpha.100" }}
                  onClick={() => navigate("/dashboard/company/profile")}
                >
                  Profile Settings
                </MenuItem>
                <MenuItem
                  icon={<FiLogOut />}
                  onClick={handleLogout}
                  bg="transparent"
                  color="red.300"
                  _hover={{ bg: "red.900", color: "red.200" }}
                >
                  Sign Out
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </Flex>
      </Box>

      {/* Desktop Sidebar */}
      <Box
        position="fixed"
        left={0}
        top={0}
        h="100vh"
        display={{ base: "none", lg: "block" }}
        zIndex={1000}
        css={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255, 255, 255, 0.2)",
            borderRadius: "4px",
          },
        }}
      >
        <SidebarContent />
      </Box>

      {/* Mobile Sidebar */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent p={0}>
          <DrawerCloseButton color="white" />
          <SidebarContent />
        </DrawerContent>
      </Drawer>

      {/* Main Content */}
      <Box
        marginLeft={SIDEBAR_WIDTH}
        pt="70px"
        minH="100vh"
        bg="#0f1419"
        position="relative"
        w={{ base: "100%", lg: "calc(100% - 280px)" }}
      >
        {/* Background Pattern */}
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.03}
          bgImage="radial-gradient(circle at 25px 25px, rgba(255,255,255,0.3) 2px, transparent 0)"
          bgSize="50px 50px"
          pointerEvents="none"
        />

        <Container maxW="full" p={6} position="relative">
          <ProgressProvider>
            <Routes>
              {/* ✅ Dashboard route - automatically shows correct dashboard */}
              <Route path="/" element={<DashboardWrapper />} />

              {/* ✅ Admin-only routes */}
              {isAdmin && (
                <>
                  <Route path="/users" element={<UserManagement />} />
                  <Route path="/profile" element={<CompanyProfile />} />
                  <Route path="/subscription" element={<SubscriptionDetails />} />
                </>
              )}

              {/* Settings - everyone can access */}
              <Route path="/settings" element={<Settings />} />

              {/* Project Management Routes - everyone can access */}
              <Route path="/projects" element={<ProjectManagement />} />
              {isAdmin && <Route path="/projects/new" element={<ProjectForm />} />}
              <Route path="/projects/:projectId" element={<ProjectDetails />} />
              {isAdmin && (
                <Route
                  path="/projects/:projectId/edit"
                  element={<ProjectForm />}
                />
              )}

              {/* Master Data Management - everyone can access */}
              <Route path="/master-data" element={<CompanyMasterDataManager />} />

              {/* Progress Routes */}
              <Route
                path="/projects/:projectId/progress-dashboard"
                element={<ProjectProgressDashboard />}
              />
              <Route
                path="/projects/:projectId/progress-report"
                element={<MonthlyProgressReport />}
              />
              <Route
                path="/projects/:projectId/progress-tracker"
                element={<ProgressTracker />}
              />

              {/* Legacy Progress Routes */}
              <Route
                path="/projects/:projectId/progress"
                element={<ProgressTracking />}
              />
              <Route
                path="/projects/:projectId/monthly"
                element={<MonthlyProgress />}
              />
              <Route
                path="/projects/:projectId/reports"
                element={<ProjectReports />}
              />

              {/* Dedicated Progress & Analytics Routes */}
              <Route path="/progress" element={<ProgressTracking />} />
              <Route path="/monthly" element={<MonthlyProgress />} />
              <Route path="/analytics" element={<ProjectReports />} />
            </Routes>
          </ProgressProvider>
        </Container>
      </Box>
    </Box>
  );
};

export default CompanyPanel;
